import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrendingUp, Building2, Briefcase } from 'lucide-react';
function Counter({
  from,
  to,
  duration = 2,
  prefix = '',
  suffix = ''
}: {
  from: number;
  to: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(from);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * (to - from) + from));
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    }
  }, [isInView, from, to, duration]);
  return <span ref={ref} data-id="element-144">
      {prefix}
      {count}
      {suffix}
    </span>;
}
export function InvestmentSection() {
  const pillars = [{
    title: 'Investment & Growth',
    description: 'Strategic capital allocation to scale proven concepts and incubate innovative culinary ventures.',
    icon: <TrendingUp size={40} strokeWidth={1} className="text-brand-gold" data-id="element-145" />,
    stat: {
      value: 50,
      prefix: '$',
      suffix: 'M+'
    },
    statLabel: 'Assets Under Management',
    href: '/advisory/investment'
  }, {
    title: 'Real Estate',
    description: 'Acquisition and development of prime hospitality real estate to secure long-term operational stability.',
    icon: <Building2 size={40} strokeWidth={1} className="text-brand-gold" data-id="element-146" />,
    stat: {
      value: 15,
      prefix: '',
      suffix: '+'
    },
    statLabel: 'Prime Properties',
    href: '/advisory/real-estate'
  }, {
    title: 'Management Company',
    description: 'Comprehensive operational oversight, standardizing excellence across all portfolio properties.',
    icon: <Briefcase size={40} strokeWidth={1} className="text-brand-gold" data-id="element-147" />,
    stat: {
      value: 300,
      prefix: '',
      suffix: '+'
    },
    statLabel: 'Team Members',
    href: '/advisory/management'
  }];
  return <section id="advisory" className="py-32 md:py-40 bg-brand-red relative overflow-hidden" data-id="element-148">
      {/* Noise Overlay */}
      <div className="absolute inset-0 bg-noise pointer-events-none z-20 opacity-40" data-id="element-149"></div>

      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: 'radial-gradient(#D3AE6E 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-150"></div>
      <div className="absolute top-0 right-0 w-2/3 h-full bg-brand-dark/20 skew-x-12 translate-x-32 z-0" data-id="element-151"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-152">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 md:mb-32 gap-12" data-id="element-153">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-154">
            <h2 className="text-5xl md:text-6xl font-serif text-white mb-8 leading-tight" data-id="element-155">
              Advisory & <br data-id="element-156" />
              <span className="text-brand-gold italic" data-id="element-157">Investment</span>
            </h2>
            <div className="w-24 h-1 bg-brand-gold" data-id="element-158"></div>
          </motion.div>

          <motion.p className="text-white/80 max-w-lg font-light leading-relaxed text-xl" initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-159">
            Leveraging over a decade of operational excellence to build
            sustainable value in the hospitality sector.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12" data-id="element-160">
          {pillars.map((pillar, index) => <motion.div key={index} className="relative" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.8,
          delay: index * 0.2,
          ease: 'easeOut'
        }} data-id="element-161">
              {/* Gold Rule */}
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-brand-gold/50 to-transparent" data-id="element-162"></div>

              <div className="pt-10" data-id="element-163">
                <div className="mb-8 bg-brand-dark/20 w-20 h-20 rounded-full flex items-center justify-center backdrop-blur-sm border border-brand-gold/20" data-id="element-164">
                  {pillar.icon}
                </div>
                <h3 className="text-3xl font-serif text-white mb-6" data-id="element-165">
                  {pillar.title}
                </h3>
                <p className="text-white/70 font-light leading-relaxed mb-10 min-h-[80px]" data-id="element-166">
                  {pillar.description}
                </p>

                <div className="mb-10" data-id="element-167">
                  <div className="text-4xl font-serif text-brand-gold mb-2" data-id="element-168">
                    <Counter from={0} to={pillar.stat.value} duration={2} prefix={pillar.stat.prefix} suffix={pillar.stat.suffix} data-id="element-169" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50" data-id="element-170">
                    {pillar.statLabel}
                  </div>
                </div>

                <Link to={pillar.href} className="inline-flex items-center text-brand-gold text-sm uppercase tracking-[0.2em] hover:text-white transition-colors group" data-id="element-171">
                  Learn More
                  <span className="ml-3 transform group-hover:translate-x-2 transition-transform duration-300" data-id="element-172">
                    →
                  </span>
                </Link>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}